import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Code2,
  Instagram,
  Linkedin,
  Menu,
  Twitter,
  X,
  Youtube,
  Download,
  ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ThemeToggle";
const LogoLight = "/logoq.webp?v=2";
const LogoDark = "/logo3.webp?v=2";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Courses", path: "/courses" },
  {
    name: "Internship",
    path: "/internship",
    subLinks: [
      { name: "Web Developer", path: "/internship/web-developer" }
    ]
  },
  { name: "Workshops", path: "/workshops" },
  { name: "Blog", path: "/blog" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

const socialLinks = [
  {
    name: "LinkedIn",
    href: "https://share.google/wmFEaCPNm9dixr94Y",
    icon: Linkedin,
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/@codoai",
    icon: Youtube,
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/codo.ai/",
    icon: Instagram,
  },
];

const floatingPillClasses =
  "group relative overflow-hidden rounded-full border border-border/40 bg-white/90 backdrop-blur-md text-foreground shadow-[0_22px_50px_-24px_rgba(28,53,102,0.55)] dark:border-white/10 dark:bg-[#10182C]/90 dark:text-white dark:shadow-[0_24px_55px_-28px_rgba(4,9,24,0.85)] before:absolute before:inset-0 before:-z-10 before:rounded-full before:bg-[linear-gradient(120deg,rgba(221,228,255,0.95)_0%,rgba(42,201,160,0.35)_25%,rgba(37,135,220,0.32)_50%,rgba(221,228,255,0.95)_75%,rgba(42,201,160,0.35)_100%)] before:bg-[length:320%_320%] before:bg-[position:-120%_50%] before:transition-[background-position,opacity] before:duration-500 before:opacity-95 before:animate-none before:content-[''] dark:before:bg-[linear-gradient(140deg,rgba(16,24,44,0.95)_0%,rgba(42,201,160,0.4)_35%,rgba(37,135,220,0.45)_70%,rgba(16,24,44,0.95)_100%)] hover:before:animate-gradient-sweep";

const mobilePanelClasses =
  "relative overflow-hidden rounded-2xl border border-border/40 bg-background/95 shadow-[0_30px_70px_-40px_rgba(28,53,102,0.65)] dark:border-white/10 dark:bg-[#0B1222]/95 dark:shadow-[0_30px_70px_-40px_rgba(4,8,20,0.9)] before:absolute before:inset-0 before:-z-10 before:bg-[linear-gradient(160deg,rgba(221,228,255,0.4),rgba(37,135,220,0.25),rgba(42,201,160,0.28))] before:bg-[length:220%_220%] before:bg-[position:-100%_50%] before:opacity-70 before:animate-gradient-pan";

const Navbar = () => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed top-2 z-50 w-full"
      aria-label="Primary navigation"
    >
      <div className="container relative px-4">
        <div className="flex items-center justify-between gap-3">
          <motion.div
            transition={{ duration: 0.2 }}
            className={`flex flex-shrink-0 items-center px-3 py-1.5 sm:px-5 sm:py-2.5 ${floatingPillClasses}`}
          >
            <Link
              to="/"
              className="relative z-10 flex items-center gap-2"
              aria-label="CODO home"
            >
              <div className="relative h-7 w-20 sm:h-9 sm:w-28">
                <img
                  src={LogoLight}
                  alt="CODO Academy Logo - Software Training Institute in Kerala"
                  width="112"
                  height="36"
                  className="block h-full w-full object-contain dark:hidden"
                  fetchpriority="high"
                />
                <img
                  src={LogoDark}
                  alt="CODO Academy Logo - Software Training Institute in Kerala (Dark Mode)"
                  width="112"
                  height="36"
                  className="hidden h-full w-full object-contain dark:block"
                  fetchpriority="high"
                />
              </div>
            </Link>
          </motion.div>

          <div className="hidden flex-1 items-center justify-center lg:flex">
            <motion.div
              transition={{ duration: 0.2 }}
              className={`inline-flex items-center px-4 py-1.5 sm:px-6 sm:py-2.5 ${floatingPillClasses} !overflow-visible`}
            >
              <span className="relative z-10 mr-3 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-muted text-foreground shadow-inner shadow-black/10">
                <Code2 className="h-4 w-4" />
              </span>
              <motion.ul
                className="relative z-10 flex items-center gap-1"
                role="list"
              >
                {navLinks.map((link) => {
                  const isActive =
                    link.path === "/"
                      ? pathname === link.path
                      : pathname.startsWith(link.path);
                  return (
                    <motion.li key={link.path} className="relative group/navlink">
                      {isActive && (
                        <motion.div
                          layoutId="nav-active-pill"
                          className="absolute inset-0 rounded-full border border-primary/20 bg-primary/15"
                          transition={{
                            type: "spring",
                            stiffness: 350,
                            damping: 30,
                          }}
                        />
                      )}
                      <Link
                        to={link.path}
                        className="relative z-10 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/70 transition-colors hover:text-primary dark:text-white/80"
                        aria-current={isActive ? "page" : undefined}
                      >
                        {link.name}
                        {link.subLinks && <ChevronDown className="ml-1 h-3 w-3" />}
                      </Link>
                      {link.subLinks && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover/navlink:opacity-100 group-hover/navlink:visible transition-all duration-200 z-50">
                          <div className="flex flex-col gap-1 rounded-xl border border-border/40 bg-white/95 p-2 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-[#10182C]/95 w-max min-w-[150px]">
                            {link.subLinks.map((subLink) => (
                              <Link
                                key={subLink.path}
                                to={subLink.path}
                                className="rounded-lg px-4 py-2 text-center text-xs font-semibold uppercase tracking-[0.1em] text-foreground/70 hover:bg-primary/10 hover:text-primary dark:text-white/80"
                              >
                                {subLink.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.li>
                  );
                })}
              </motion.ul>
            </motion.div>
          </div>

          <motion.div
            transition={{ duration: 0.2 }}
            className="flex flex-shrink-0 items-center gap-2 sm:gap-4"
          >
            <div className="relative z-10 hidden items-center gap-3 lg:flex">
              <ThemeToggle />
              <Link to="/study-materials">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex h-9 items-center gap-2 rounded-full bg-primary px-5 text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90 shadow-lg shadow-primary/20"
                >
                  <Download className="h-4 w-4" />
                  <span>Free Materials</span>
                </motion.button>
              </Link>
            </div>
            <div className="relative z-10 flex items-center gap-2 lg:hidden">
              <ThemeToggle />
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label="Toggle navigation menu"
                type="button"
                className="flex h-9 w-10 items-center justify-center rounded-full border border-border/60 bg-background/90 text-foreground/80 transition-colors hover:border-primary/40 hover:text-primary dark:border-white/15 dark:bg-white/10 dark:text-white/80 dark:hover:border-primary/50"
              >
                <motion.span
                  key={isOpen ? "close" : "open"}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {isOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </motion.span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-20 z-40 px-4 lg:hidden"
          >
            <motion.div
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className={`${mobilePanelClasses} p-6`}
            >
              <ul className="relative z-10 space-y-1" role="list">
                {navLinks.map((link) => {
                  const isActive =
                    link.path === "/"
                      ? pathname === link.path
                      : pathname.startsWith(link.path);
                  return (
                    <li key={link.path} className="flex flex-col">
                      <Link
                        to={link.path}
                        onClick={() => !link.subLinks && setIsOpen(false)}
                        className="flex items-center justify-between rounded-md px-2 py-2 text-sm font-semibold uppercase tracking-[0.1em] text-foreground/80 transition-colors hover:text-primary dark:text-white/80"
                        aria-current={isActive ? "page" : undefined}
                      >
                        {link.name}
                        {isActive && (
                          <span
                            className="ml-2 h-1.5 w-1.5 rounded-full bg-primary"
                            aria-hidden="true"
                          />
                        )}
                      </Link>
                      {link.subLinks && (
                        <ul className="ml-4 mt-1 border-l-2 border-primary/20 pl-4 space-y-1">
                          {link.subLinks.map(sub => (
                            <li key={sub.path}>
                              <Link to={sub.path} onClick={() => setIsOpen(false)} className="block py-2 text-xs font-semibold uppercase tracking-wider text-foreground/70 hover:text-primary dark:text-white/80">
                                {sub.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="relative z-10 mt-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                </div>
                <div className="flex flex-col gap-2">
                  <Link to="/study-materials" onClick={() => setIsOpen(false)}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1.5 rounded-full border-primary/20 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-primary hover:bg-primary hover:text-white w-full"
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span>Free Materials</span>
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
